import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Stack } from '@mui/material'
import {
  ObservationLogFormData,
  ObservationLogFormActions,
} from '@/components/forms'
import {
  ControlledTextField,
  ControlledDateTimeField,
  ControlledMediaUpload,
} from '@/components/inputs'
import { SpecieFieldSet } from './SpecieFieldSet'
import { yupResolver } from '@hookform/resolvers/yup'
import { useApiErrorData } from '@/hooks/forms/useApiErrorData'
import { useAuth } from '@/hooks/useAuth'
import { FormComponentProps, UserGroups } from '@/types'
import { schema } from './validationSchema'

export const BatsFeedingAreaForm = ({
  onSubmit,
  defaultValues,
  isLoading,
  apiErrorData,
}: FormComponentProps<ObservationLogFormData>): JSX.Element => {
  const { t } = useTranslation('observationLogs')

  const { hasPermission } = useAuth()

  const methods = useForm<ObservationLogFormData>({
    mode: 'onChange',
    defaultValues: {
      specie: '',
      specie2: '',
      specie3: '',
      specie4: '',
      amount: '',
      amount2: '',
      amount3: '',
      amount4: '',
      counting_method: '',
      counting_method2: '',
      counting_method3: '',
      counting_method4: '',
      sound_recordings: undefined,
      sound_recordings2: undefined,
      sound_recordings3: undefined,
      sound_recordings4: undefined,
      behaviour_notes: '',
      recording_number: '',
      recording_number2: '',
      recording_number3: '',
      recording_number4: '',
      observed_at: '',
      photos: undefined,
    },
    values: defaultValues,
    resolver: yupResolver(schema),
  })

  useApiErrorData<ObservationLogFormData>(apiErrorData, methods)

  const disabledForTheUser = !hasPermission([
    UserGroups.MANAGER,
    UserGroups.EMPLOYEE,
    UserGroups.DATA_CONTROLLER,
  ])

  return (
    <FormProvider {...methods}>
      <Stack mt={4} width="100%" alignItems="stretch" spacing={4}>
        <SpecieFieldSet
          number=""
          methods={methods}
          disabledForTheUser={disabledForTheUser}
        />
        {methods.watch('specie') && (
          <SpecieFieldSet
            number="2"
            methods={methods}
            disabledForTheUser={disabledForTheUser}
          />
        )}
        {methods.watch('specie2') && (
          <SpecieFieldSet
            number="3"
            methods={methods}
            disabledForTheUser={disabledForTheUser}
          />
        )}
        {methods.watch('specie3') && (
          <SpecieFieldSet
            number="4"
            methods={methods}
            disabledForTheUser={disabledForTheUser}
          />
        )}

        <ControlledTextField
          control={methods.control}
          name="behaviour_notes"
          placeholder={t('observation-log.fieldLabels.behaviourNotes')}
          multiline={true}
          rows={3}
          inputProps={{
            style: {
              padding: 0,
            },
          }}
          errorMessagePrefix=""
        />
        <ControlledDateTimeField
          name="observed_at"
          control={methods.control}
          label={t('observation-log.fieldLabels.observedAt')}
          errorMessagePrefix=""
        />
        <ControlledMediaUpload
          name="photos"
          control={methods.control}
          label={t('observation-log.fieldLabels.media')}
          errorMessagePrefix=""
        />

        <ObservationLogFormActions
          onSubmit={onSubmit}
          defaultValues={defaultValues}
          isLoading={isLoading}
          methods={methods}
        />
      </Stack>
    </FormProvider>
  )
}
